import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { THIN_SCROLLBAR } from 'common/styles/listUI';
import Popover from './Popover';

const INK = '#383838';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const LINE_2 = '#eff0ee';
const GREEN = '#6c8a2e';
const GREEN_T = '#eef3e2';

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${props => (props.open ? INK_3 : LINE)};
  background: white;
  color: ${INK_2};
  font-family: inherit;
  font-size: ${props => props.fontSize};
  border-radius: 7px;
  padding: ${props => props.compact ? '4px 8px' : '6px 8px'};
  cursor: pointer;
  white-space: nowrap;
  box-sizing: border-box;

  &:hover { border-color: ${INK_3}; }
  .chev { color: ${INK_3}; font-size: 9px; margin-left: 1px; }
`;

const Menu = styled.div`
  background: white;
  border: 1px solid ${LINE};
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(40, 44, 36, 0.16);
  padding: 4px;
  max-height: 320px;
  overflow-y: auto;
  ${THIN_SCROLLBAR}
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 6px;
  font-size: 12.5px;
  cursor: pointer;
  white-space: nowrap;
  color: ${props => (props.active ? GREEN : INK)};
  background: ${props => (props.active ? GREEN_T : 'transparent')};
  font-weight: ${props => (props.active ? 700 : 400)};

  &:hover { background: ${props => (props.active ? GREEN_T : LINE_2)}; }
  .tick { width: 12px; color: ${GREEN}; font-size: 11px; }
`;

class MenuSelect extends React.Component {
  state = { open: false };

  setAnchor = (node) => { this.anchor = node; };

  getAnchor = () => this.anchor;

  toggle = () => this.setState(prev => ({ open: !prev.open }));

  close = () => this.setState({ open: false });

  pick = (value) => {
    this.close();
    if (value !== this.props.value) this.props.onChange(value);
  };

  render() {
    const {
      options, value, prefix, align, compact, title
    } = this.props;
    const { open } = this.state;
    const selected = options.find(o => o.value === value);
    const selectedLabel = selected ? selected.label : '';
    const display = prefix ? `${prefix}: ${selectedLabel}` : selectedLabel;

    return (
      <React.Fragment>
        <Trigger
          type="button"
          innerRef={this.setAnchor}
          open={open}
          compact={compact}
          fontSize={compact ? '11px' : '12px'}
          onClick={this.toggle}
          title={title}
        >
          {display}
          <span className="chev">▾</span>
        </Trigger>
        <Popover open={open} getAnchor={this.getAnchor} onClose={this.close} align={align}>
          <Menu>
            {options.map(o => (
              <Item key={o.value} active={o.value === value} onClick={() => this.pick(o.value)}>
                <span className="tick">{o.value === value ? '✓' : ''}</span>
                {o.label}
              </Item>
            ))}
          </Menu>
        </Popover>
      </React.Fragment>
    );
  }
}

MenuSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  prefix: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right']),
  compact: PropTypes.bool,
  title: PropTypes.string
};

MenuSelect.defaultProps = {
  value: '',
  prefix: '',
  align: 'left',
  compact: false,
  title: ''
};

export default MenuSelect;
